import {Chart, ChartConfiguration, ChartData, ChartOptions, Plugin} from 'chart.js';
import classNames from 'classnames';
import React from 'react';

import cs from 'app/components/Dashboard/styles/chart.module.scss';
import * as hookUtils from 'app/utils/hookUtils';

const GenericPieChart: React.FunctionComponent<
  React.PropsWithChildren<{
    labels: ChartData<'pie' | 'doughnut'>['labels'];
    datasets: ChartData<'pie' | 'doughnut'>['datasets'];

    className?: string;
    type?: 'pie' | 'doughnut';
    options?: ChartOptions<'pie' | 'doughnut'>;
    plugins?: Plugin<'pie' | 'doughnut'>[];
    isExpanded?: boolean;
    renderLegend?: () => React.ReactNode;
    controls?: React.ReactNode;
    displayTitle?: string;
  }>
> = ({
  labels,
  datasets,
  className,
  type = 'pie',
  options = {},
  plugins = [],
  renderLegend,
  controls,
  displayTitle,
}) => {
  const chartRef = React.useRef<Chart<'pie' | 'doughnut'> | null>(null);

  const stableLabels = hookUtils.useContinuity(labels);
  const stableOptions = hookUtils.useContinuity(options);

  const combinedOptions = React.useMemo<ChartOptions<'pie' | 'doughnut'>>(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: !renderLegend,
        },
      },
      animation: {
        duration: 0,
      },
      ...stableOptions,
    }),
    [stableOptions, renderLegend]
  );

  const refCallback = React.useCallback(
    (node: HTMLCanvasElement | null) => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }

      if (node) {
        const ctx = node.getContext('2d');
        const config: ChartConfiguration<'pie' | 'doughnut'> = {
          type,
          data: {
            labels: stableLabels,
            datasets: datasets,
          },
          plugins,
          options: combinedOptions,
        };

        if (ctx) {
          chartRef.current = new Chart(ctx, config);
        }
      }
    },
    [type, stableLabels, datasets, plugins, combinedOptions]
  );

  React.useEffect(() => {
    if (chartRef.current) {
      chartRef.current.data.labels = stableLabels;
      chartRef.current.data.datasets = datasets;
      chartRef.current.options = combinedOptions;
      chartRef.current.update();
    }
  }, [stableLabels, datasets, combinedOptions, chartRef]);

  return (
    <div className={classNames(cs.pieChartContainer, className)}>
      <div>
        <div className={cs.pieChartCanvas}>
          <canvas ref={refCallback} />
        </div>
        {displayTitle && <div className={cs.pieChartTitle}>{displayTitle}</div>}
      </div>
      <div className={cs.pieChartControlsAndLegend}>
        {controls}
        {!!renderLegend && (
          <div id="legend" style={{zIndex: 1}}>
            {renderLegend()}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericPieChart;
